<template>
  <div>
    <!-- 项目搜索弹窗 -->
    <div v-if="show" class="dialog-overlay" @click="$emit('close')">
      <div class="dialog project-dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ $t('mobile.projectList.title') }}</h3>
          <button class="close-btn" @click="$emit('close')">×</button>
        </div>
        
        <div class="dialog-content">
          <section class="project-guide" :class="{ 'is-expanded': guideExpanded }">
            <div class="project-guide-summary">
              <div class="project-guide-copy">
                <strong>{{ $t('mobile.projectList.guide.summaryTitle') }}</strong>
                <p>{{ $t('mobile.projectList.guide.summary') }}</p>
              </div>
              <button
                type="button"
                class="project-guide-toggle"
                :aria-expanded="guideExpanded"
                aria-controls="project-guide-details"
                @click="guideExpanded = !guideExpanded"
              >
                <span>
                  {{ guideExpanded
                    ? $t('mobile.projectList.guide.collapse')
                    : $t('mobile.projectList.guide.expand') }}
                </span>
                <svg
                  class="project-guide-chevron"
                  :class="{ 'is-expanded': guideExpanded }"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

            <div v-if="guideExpanded" id="project-guide-details" class="project-guide-details">
              <p class="project-guide-details-title">{{ $t('mobile.projectList.guide.stepsTitle') }}</p>
              <ol class="project-guide-steps">
                <li>
                  <span class="project-guide-step-number">1</span>
                  <p>{{ $t('mobile.projectList.guide.stepOne') }}</p>
                </li>
                <li>
                  <span class="project-guide-step-number">2</span>
                  <p>{{ $t('mobile.projectList.guide.stepTwo') }}</p>
                </li>
                <li>
                  <span class="project-guide-step-number">3</span>
                  <p>{{ $t('mobile.projectList.guide.stepThree') }}</p>
                </li>
              </ol>

              <div class="project-guide-example">
                <strong>{{ $t('mobile.projectList.guide.exampleTitle') }}</strong>
                <p>{{ $t('mobile.projectList.guide.exampleSms') }}</p>
                <p>{{ $t('mobile.projectList.guide.exampleSearch') }}</p>
                <p>{{ $t('mobile.projectList.guide.exampleChoice') }}</p>
              </div>

              <div class="project-guide-support">
                <strong>{{ $t('mobile.projectList.guide.missingTitle') }}</strong>
                <p>{{ $t('mobile.projectList.guide.missingContent') }}</p>
              </div>

              <p class="project-guide-warning">{{ $t('mobile.projectList.guide.warning') }}</p>
            </div>
          </section>

          <!-- 项目列表 -->
          <div v-if="!loading && projectList.length > 0" class="project-list">
            <div 
              v-for="project in projectList" 
              :key="project.id + '-' + (project.sid || 'default')"
              class="project-row"
              @click="handleProjectClick(project)"
            >
              <div class="project-info">
                <div class="project-main">
                  <h4 class="project-name">{{ project.name }}</h4>
                  <div class="project-tags">
                    <span class="project-tag">ID: {{ project.id }}</span>
                    <!-- <span v-if="project.sid" class="project-tag">SID: {{ project.sid }}</span> -->
                    <span class="status-tag available">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="currentColor"/>
                      </svg>
                      {{ $t('mobile.projectList.available') }}
                    </span>
                  </div>
                </div>
              </div>
              <button class="view-channels-btn" @click.stop="$emit('show-channels', project.id, project.sid)">
                {{ $t('mobile.projectList.viewChannels') }}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          
          <div v-else-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('common.searching') }}</p>
          </div>
          
          <div v-else-if="searched && projectList.length === 0" class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" stroke-width="2"/>
              <path d="M21 21L16.65 16.65" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p>{{ $t('project.noProjectsFound') }}</p>
            <div class="empty-state-hint">
              <p>{{ $t('project.noProjectsHint') }}</p>
            </div>
            <button class="btn btn-primary" @click="$emit('apply-project')">
              {{ $t('applyProject.applyButton') }}
            </button>
          </div>
          
          <div v-else class="empty-state">
            <p>{{ $t('project.enterKeywords') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectList',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    projectList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    searched: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      guideExpanded: false
    }
  },
  watch: {
    show(isVisible) {
      if (!isVisible) {
        this.guideExpanded = false
      }
    }
  },
  methods: {
    handleProjectClick(project) {
      this.$emit('select-project', project);
    }
  }
}
</script>

<style scoped>
/* 项目对话框 */
.project-dialog {
  max-width: 900px;
  width: 90%;
  max-height: min(90vh, 760px);
}

.project-dialog .dialog-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow: hidden;
}

.project-guide {
  flex: 0 0 auto;
  border: 1px solid #D8E2FF;
  border-left: 4px solid #4461F2;
  border-radius: 8px;
  background: #F7F9FF;
  color: #1F2937;
}

.project-guide-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
}

.project-guide-copy {
  min-width: 0;
}

.project-guide-copy strong {
  display: block;
  font-size: 14px;
  line-height: 1.5;
}

.project-guide-copy p {
  margin: 4px 0 0;
  color: #4B5563;
  font-size: 13px;
  line-height: 1.55;
}

.project-guide-toggle {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 8px 4px 8px 8px;
  border: 0;
  background: transparent;
  color: #3154D8;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-align: right;
}

.project-guide-toggle:hover {
  color: #1E3A8A;
}

.project-guide-toggle:focus-visible {
  outline: 2px solid #4461F2;
  outline-offset: 2px;
  border-radius: 4px;
}

.project-guide-chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.project-guide-chevron.is-expanded {
  transform: rotate(180deg);
}

.project-guide-details {
  max-height: 300px;
  overflow-y: auto;
  padding: 0 16px 16px;
  border-top: 1px solid #E1E8FF;
  color: #374151;
  font-size: 13px;
  line-height: 1.6;
}

.project-guide-details-title {
  margin: 14px 0 8px;
  color: #1F2937;
  font-weight: 600;
}

.project-guide-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.project-guide-steps li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.project-guide-step-number {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #4461F2;
  color: #FFFFFF;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.project-guide-steps p,
.project-guide-example p,
.project-guide-support p {
  margin: 0;
}

.project-guide-example,
.project-guide-support {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #E1E8FF;
}

.project-guide-example strong,
.project-guide-support strong {
  display: block;
  margin-bottom: 4px;
  color: #1F2937;
}

.project-guide-warning {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-left: 3px solid #D97706;
  background: #FFF7ED;
  color: #92400E;
}

.project-dialog .project-list {
  flex: 1 1 auto;
  min-height: 160px;
  max-height: none;
}

.project-dialog .loading-state,
.project-dialog .empty-state {
  flex: 1 1 auto;
  min-height: 160px;
}

/* 项目列表布局 */
.project-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px;
}

/* 项目行 */
.project-row {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.project-row:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(68, 97, 242, 0.15);
  border-color: #4461F2;
  background: linear-gradient(90deg, rgba(68, 97, 242, 0.02) 0%, transparent 100%);
}

.project-info {
  flex: 1;
  min-width: 0;
}

.project-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.project-name {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
  word-wrap: break-word;
  word-break: break-word;
}

.project-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.project-tag {
  font-size: 12px;
  color: #6B7280;
  background: #F3F4F6;
  padding: 2px 8px;
  border-radius: 4px;
}

.view-channels-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.view-channels-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.3);
}

.view-channels-btn svg {
  flex-shrink: 0;
}

.project-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #F3F4F6;
  border-bottom: 1px solid #F3F4F6;
}

.project-price {
  font-size: 18px;
  font-weight: 700;
  color: #EF4444;
}

.project-count {
  font-size: 14px;
  color: #6B7280;
}

.project-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.available {
  background: #D1FAE5;
  color: #065F46;
}

.project-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.btn-primary-gradient {
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-primary-gradient:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 移动端样式 */
@media (max-width: 768px) {
  .project-dialog {
    max-width: 100%;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    margin: 0;
  }

  .project-dialog .dialog-content {
    gap: 12px;
    padding: 16px;
  }

  .project-guide-summary {
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }

  .project-guide-copy p {
    font-size: 13px;
  }

  .project-guide-toggle {
    max-width: 132px;
    padding-right: 0;
    font-size: 12px;
  }

  .project-guide-details {
    max-height: 260px;
    padding: 0 12px 12px;
    font-size: 13px;
  }

  .project-dialog .project-list,
  .project-dialog .loading-state,
  .project-dialog .empty-state {
    min-height: 140px;
  }
}
</style>
